import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LuPlus, LuMinus } from 'react-icons/lu';
import useInView from '../hooks/useInView';

const FAQS = [
  {
    q: 'Is this legal?',
    a: (
      <>
        <p>
          Yes. You're sending peer-to-peer SMS from your own SIM card, under your own carrier contract — exactly like if you texted someone from your phone manually. TextLink is just an automation layer on top.
        </p>
        <p>
          You still need recipient consent (like any SMS) and should include an opt-out (<em>"Reply STOP"</em>) where legally required. Same rules every business follows. The difference is there's no 10DLC / A2P registration needed, because the traffic isn't classified as A2P — it's your personal line.
        </p>
      </>
    )
  },
  {
    q: 'Will my SIM get banned by the carrier?',
    a: (
      <>
        <p>
          It can happen if you spam people — that's true of any SMS channel, aggregator or direct. To prevent it TextLink includes <strong>built-in SMS warmup</strong> that ramps your volume gradually over 21 days, the same way email warmup works.
        </p>
        <p>
          Send to opted-in contacts, vary your message text, include opt-outs, and you'll be fine. We publish concrete rate limits and carrier-specific advice inside the dashboard.
        </p>
      </>
    )
  },
  {
    q: 'What if my phone dies or loses Wi-Fi?',
    a: (
      <>
        <p>
          New sends queue in our servers and deliver the moment the device reconnects. Inbound messages on the SIM are stored by the carrier (standard SMS behavior) and sync back to your dashboard when the phone comes online.
        </p>
        <p>
          For production setups we recommend a dedicated phone on a charger with stable Wi-Fi — a used Android for $40 is plenty. Multi-device accounts fail over automatically.
        </p>
      </>
    )
  },
  {
    q: 'Can I use multiple SIMs, or a dual-SIM phone?',
    a: (
      <>
        <p>
          Both. Plans scale from 3 SIMs to unlimited. You can use one phone with a dual-SIM setup, or multiple phones each with their own SIMs — the dashboard treats them as a pool and auto-routes per contact so each recipient always gets messages from the same number.
        </p>
      </>
    )
  },
  {
    q: 'Does it work with GoHighLevel / Zapier / my API?',
    a: (
      <>
        <p>
          <strong>GoHighLevel</strong> — native integration. TextLink shows up as a Conversation Provider; conversations, campaigns, and workflows use it automatically. <Link to="/ghl">See GHL setup</Link>.
        </p>
        <p>
          <strong>Zapier / Make / n8n</strong> — via our <Link to="/smsapi">REST API</Link> and webhooks. Four lines to send; webhooks for sent, delivered, received.
        </p>
        <p>
          <strong>Custom integrations</strong> — same REST API, SDKs for JavaScript, Python, C#.
        </p>
      </>
    )
  }
];

export default function HomeFAQ() {
  const [ref, inView] = useInView();
  const [open, setOpen] = useState(0);

  return (
    <section ref={ref} className={`hfq${inView ? ' in-view' : ''}`}>
      <div className="hfq-container">
        <header className="hfq-head">
          <span className="hfq-eyebrow">COMMON QUESTIONS</span>
          <h2 className="hfq-title">The objections, answered.</h2>
          <p className="hfq-sub">
            If you're considering TextLink, you're probably asking one of these. Click through for short, honest answers.
          </p>
        </header>

        <ul className="hfq-list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <li key={i} className={`hfq-item${isOpen ? ' hfq-item--open' : ''}`} style={{ '--i': i }}>
                <button
                  className="hfq-q"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                >
                  <span>{item.q}</span>
                  <span className="hfq-icon">
                    {isOpen ? <LuMinus size={15} strokeWidth={2.25} /> : <LuPlus size={15} strokeWidth={2.25} />}
                  </span>
                </button>
                <div className="hfq-a" aria-hidden={!isOpen}>
                  <div className="hfq-a-inner">{item.a}</div>
                </div>
              </li>
            );
          })}
        </ul>

        <p className="hfq-foot">
          More to ask? The chat in the bottom right answers fast, or{' '}
          <Link to="/faq">see the full FAQ</Link>.
        </p>
      </div>

      <style jsx>{`
        .hfq {
          background: #fbfbfa;
          padding: clamp(5rem, 12vh, 9rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #0a0a0a;
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.005em;
        }
        .hfq-container { max-width: 780px; margin: 0 auto; }

        .hfq-head { text-align: center; margin-bottom: clamp(2.5rem, 5vh, 3.5rem); }
        .hfq-eyebrow {
          display: inline-block;
          font-size: 0.6875rem; font-weight: 600;
          letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(0,0,0,0.5);
          margin-bottom: 1.25rem;
        }
        .hfq-title {
          font-size: clamp(2rem, 4.8vw, 3.25rem);
          font-weight: 500;
          letter-spacing: -0.035em;
          line-height: 1.05;
          margin: 0 0 0.75rem;
        }
        .hfq-sub {
          font-size: clamp(0.9375rem, 1.4vw, 1.0625rem);
          color: rgba(0,0,0,0.55);
          line-height: 1.55;
          margin: 0 auto;
          max-width: 540px;
        }

        .hfq-head, .hfq-item, .hfq-foot {
          opacity: 0;
          translate: 0 12px;
          transition: opacity 620ms cubic-bezier(0.16, 1, 0.3, 1), translate 620ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hfq.in-view .hfq-head { opacity: 1; translate: 0 0; }
        .hfq.in-view .hfq-item { opacity: 1; translate: 0 0; transition-delay: calc(120ms + var(--i, 0) * 60ms); }
        .hfq.in-view .hfq-foot { opacity: 1; translate: 0 0; transition-delay: 500ms; }

        .hfq-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .hfq-item {
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.07);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 200ms ease, box-shadow 200ms ease, opacity 620ms cubic-bezier(0.16, 1, 0.3, 1), translate 620ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hfq-item:hover { border-color: rgba(0,0,0,0.14); }
        .hfq-item--open {
          border-color: rgba(124, 58, 237, 0.25);
          box-shadow: 0 10px 24px -16px rgba(124, 58, 237, 0.2);
        }

        .hfq-q {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          background: transparent;
          border: none;
          padding: 1rem 1.25rem;
          font-family: inherit;
          font-size: 1rem;
          font-weight: 500;
          letter-spacing: -0.015em;
          color: #0a0a0a;
          text-align: left;
          cursor: pointer;
        }
        .hfq-icon {
          width: 28px; height: 28px; border-radius: 50%;
          background: rgba(0,0,0,0.04);
          color: rgba(0,0,0,0.55);
          display: inline-flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: background 180ms ease, color 180ms ease;
        }
        .hfq-item--open .hfq-icon {
          background: #ede9fe;
          color: #6d28d9;
        }

        .hfq-a {
          max-height: 0;
          overflow: hidden;
          transition: max-height 360ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hfq-item--open .hfq-a { max-height: 500px; }
        .hfq-a-inner {
          padding: 0 1.25rem 1.25rem;
          font-size: 0.9375rem;
          line-height: 1.6;
          color: rgba(0,0,0,0.68);
        }
        .hfq-a-inner p { margin: 0 0 0.75rem; }
        .hfq-a-inner p:last-child { margin-bottom: 0; }
        .hfq-a-inner strong { color: #0a0a0a; font-weight: 600; }
        .hfq-a-inner :global(a) {
          color: #6d28d9;
          text-decoration: underline;
          text-underline-offset: 2px;
          font-weight: 500;
        }

        .hfq-foot {
          margin: clamp(2rem, 4vh, 2.75rem) 0 0;
          text-align: center;
          font-size: 0.875rem;
          color: rgba(0,0,0,0.55);
        }
        .hfq-foot :global(a) {
          color: #0a0a0a;
          text-decoration: underline;
          text-underline-offset: 2px;
          font-weight: 500;
        }
      `}</style>
    </section>
  );
}
